/**
 * MyProjectsCardsPage — Leader split-screen view for /my-projects.
 *
 * Left pane:  Project cards filtered to OWNER/LEAD projects with KPI shape
 *             indicators and CreateProjectCard.
 * Right pane: Compact ProjectDetailPanel (viewMode="leader") showing
 *             management subtabs for the selected project.
 *
 * DEV-649 / DEV-652 / DEV-657
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';

import ProjectCard from '../../Components/ProjectCard/ProjectCard';
import type { CommunityPathSegment } from '../../Components/ProjectCard/ProjectCard';
import CreateProjectCard from '../../Components/ProjectStepper/CreateProjectCard';
import CreateMiniProjectModal from '../../Components/CreateMiniProjectModal/CreateMiniProjectModal';
import cardStyles from '../../Components/ProjectCard/ProjectCard.module.css';
import ProjectDetailPanel from '../ProjectDetail/ProjectDetailPanel';
import MiniProjectDetailPanel from '../MiniProjectDetail/MiniProjectDetailPanel';
import ResizableSplitPane from '../../Components/ResizableSplitPane/ResizableSplitPane';
import { resolveCommunityAncestors } from '../../Data/ProjectConfig/projectConfigOverrides';
import { communityConfigStore } from '../../Data/store';
import {
  updateMiniProject,
  dispatchMiniProjectMutated,
} from '../../api/miniProjects';
import type { MiniProjectAutoOpenKpi } from '../MiniProjectDetail/MiniProjectDetailPanel';

import styles from './MyProjectsCardsPage.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

type Props = {
  data: any[];
  currentUserGuid: string;
  /** Project ACL entries (seed-shape: { project_guid, user_guid, role, is_active }). */
  apiProjectAcl?: Record<string, any>[];
  /** Community entities for country-flag resolution on project cards. */
  apiCommunityEntities?: Record<string, any>[];
  /** Parent-child relationships for walking entity hierarchy to find country flags. */
  apiCommunityRelationships?: Record<string, any>[];
  debugMode?: boolean;
  onProjectConfigChanged?: () => void;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Roles that qualify a project for "My Projects". */
const MY_ROLES = new Set(['owner_admin', 'lead']);

/** localStorage key for the show-completed toggle. */
const LS_SHOW_COMPLETED_KEY = 'l4e_myProjectsCards_showCompleted';

/** Convert an ISO 3166-1 alpha-2 country code to its flag emoji. */
function countryFlagEmoji(code: string | null | undefined): string {
  if (!code || code.length < 2) return '';
  const upper = code.toUpperCase().trim();
  const cc = upper.indexOf('-') === 2 ? upper.slice(0, 2) : (upper.length === 2 ? upper : '');
  if (!cc || !/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...Array.from(cc).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function MyProjectsCardsPage(props: Props) {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();
  const location = useLocation();
  const restoreGuid = (location.state as any)?.restoreProjectGuid as string | undefined;
  const restoreWorkSubTab = (location.state as any)?.workSubTab as string | undefined;
  const {
    data,
    currentUserGuid,
    apiProjectAcl,
    apiCommunityEntities,
    apiCommunityRelationships,
    debugMode,
    onProjectConfigChanged,
  } = props;

  const [searchText, setSearchText] = useState('');
  const [showCreateMini, setShowCreateMini] = useState(false);
  const [selectedGuid, setSelectedGuid] = useState<string | null>(
    restoreGuid ? restoreGuid.toUpperCase() : null,
  );
  const [activeKpiTabIndex, setActiveKpiTabIndex] = useState(-1);
  const [tabRequestId, setTabRequestId] = useState(0);

  /* ---- DEV-1260: when the selected row is a Mini-Project, which KPI
     modal (if any) the right-pane panel should auto-open. KPI pill index
     mapping mirrors PortfolioPage; Summary (index 0) just selects. ---- */
  const [miniAutoOpenKpi, setMiniAutoOpenKpi] = useState<MiniProjectAutoOpenKpi>(null);

  /* ---- Show/hide completed projects (Skip request, mirrors the
     KpiGridTable show-completed pill). Persisted so the preference
     survives navigation and refresh. ---- */
  const [showCompleted, setShowCompleted] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_SHOW_COMPLETED_KEY) !== 'false'; }
    catch { return true; }
  });
  const toggleShowCompleted = useCallback(() => {
    setShowCompleted((prev) => {
      const next = !prev;
      try { localStorage.setItem(LS_SHOW_COMPLETED_KEY, String(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  /* Auto-select project when returning from full-page view via restore */
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoreGuid && !restoredRef.current) {
      restoredRef.current = true;
      setSelectedGuid(restoreGuid.toUpperCase());
      setTabRequestId((n) => n + 1);
    }
  }, [restoreGuid]);

  /* ---- Build set of project GUIDs where user is OWNER or LEAD ---- */
  const myProjectGuids = useMemo(() => {
    const set = new Set<string>();
    const me = currentUserGuid.toUpperCase();
    if (!me) return set;
    for (const acl of apiProjectAcl || []) {
      const role = String(acl.role || '').toLowerCase();
      if (
        String(acl.user_guid || '').toUpperCase() === me &&
        acl.is_active !== false &&
        MY_ROLES.has(role)
      ) {
        set.add(String(acl.project_guid || '').toUpperCase());
      }
    }
    return set;
  }, [apiProjectAcl, currentUserGuid]);

  /* ---- Role label for the current user per project ---- */
  const userRoleByProject = useMemo(() => {
    const m = new Map<string, string>();
    const me = currentUserGuid.toUpperCase();
    if (!me) return m;
    for (const acl of apiProjectAcl || []) {
      if (String(acl.user_guid || '').toUpperCase() === me && acl.is_active !== false) {
        m.set(String(acl.project_guid || '').toUpperCase(), String(acl.role || 'viewer'));
      }
    }
    return m;
  }, [apiProjectAcl, currentUserGuid]);

  /* ---- Filter data to only my projects ----
     DEV-1260: Data carries Mini-Project rows too (App.tsx aggregator,
     `_isMiniProject` flag, FP-shaped `project` object + KPI indicator
     fields). MPs have no ACL rows — "mine" = I am the Lead, surfaced by
     the API as ownerUserGuid on the spread MiniProject. Masters never
     reach here (list endpoint excludes them). */
  const myProjects = useMemo(() => {
    const me = currentUserGuid.toUpperCase();
    return (data || []).filter((row: any) => {
      if (row?._isMiniProject) {
        return String(row?.project?.ownerUserGuid || '').toUpperCase() === me;
      }
      const guid = String(row?.project_guid || row?.project_GUID || '').toUpperCase();
      return myProjectGuids.has(guid);
    });
  }, [data, myProjectGuids, currentUserGuid]);

  /* ---- Search ---- */
  const searchTerms = useMemo(() => {
    const raw = String(searchText || '').trim().toLowerCase();
    if (!raw) return [] as string[];
    return raw.split(/\s+/).filter(Boolean);
  }, [searchText]);

  const filteredProjects = useMemo(() => {
    // Show/hide completed runs before search. MP rows: the App.tsx
    // aggregator normalizes lifecycle-complete to project_status
    // 'Completed' (and sets _isCompletedProject), so one check covers
    // both kinds.
    const base = showCompleted
      ? myProjects
      : myProjects.filter((row: any) => {
          const p = row?.project || row || {};
          const status = String(p.project_status ?? '').toLowerCase();
          return status !== 'completed' && !row?._isCompletedProject;
        });
    if (searchTerms.length === 0) return base;
    return base.filter((row: any) => {
      const p = row?.project || row || {};
      const blob = [
        p.project_name || '',
        p.project_category || '',
        p.project_status || '',
        p.description || '',
        p.Community || p.community_l3 || '',
      ].join(' ').toLowerCase();
      return searchTerms.every((term) => blob.includes(term));
    });
  }, [myProjects, searchTerms, showCompleted]);


  /* ---- Resolve selected project row from data ---- */
  const selectedProjectRow = useMemo(() => {
    if (!selectedGuid) return null;
    return (data || []).find(
      (d: any) => String(d?.project_guid ?? d?.project_GUID ?? '').toUpperCase() === selectedGuid.toUpperCase()
    ) ?? null;
  }, [data, selectedGuid]);

  /* ---- If the selected project disappears from the data set (e.g. deleted), clear selection ---- */
  useEffect(() => {
    if (selectedGuid && !selectedProjectRow) {
      setSelectedGuid(null);
    }
  }, [selectedGuid, selectedProjectRow]);

  /* ---- Entity / parent maps for country flag resolution ---- */
  const { entityMap, parentMap } = useMemo(() => {
    const em = new Map<string, Record<string, any>>();
    for (const e of apiCommunityEntities || []) {
      em.set(String(e.entity_guid || '').toUpperCase(), e);
    }
    const pm = new Map<string, string>();
    for (const r of apiCommunityRelationships || []) {
      pm.set(
        String(r.child_entity_guid || '').toUpperCase(),
        String(r.parent_entity_guid || '').toUpperCase()
      );
    }
    return { entityMap: em, parentMap: pm };
  }, [apiCommunityEntities, apiCommunityRelationships]);

  const resolveCountryInfo = useCallback(
    (primaryLocationGuid: string | null | undefined): { flag: string; name: string } => {
      const empty = { flag: '', name: '' };
      if (!primaryLocationGuid) return empty;
      const startGuid = primaryLocationGuid.toUpperCase();
      const startEntity = entityMap.get(startGuid);
      const localName = startEntity ? String(startEntity.name || '') : '';

      let guid: string | undefined = startGuid;
      for (let i = 0; i < 6 && guid; i++) {
        const entity = entityMap.get(guid);
        if (!entity) break;
        const etype = String(entity.entity_type || '').toLowerCase();
        const code = entity.code;
        if (etype === 'geo_country' && code) {
          const flag = countryFlagEmoji(code);
          return { flag, name: localName || String(entity.name || code) };
        }
        if (code) {
          const flag = countryFlagEmoji(code);
          if (flag) return { flag, name: localName || String(entity.name || code) };
        }
        guid = parentMap.get(guid);
      }
      return empty;
    },
    [entityMap, parentMap]
  );

  /* ---- Build community path for a project (leaf + ancestors, single line) ---- */
  const buildCommunityPath = useCallback(
    (locationGuid: string): CommunityPathSegment[] => {
      if (!locationGuid) return [];
      const guid = locationGuid.toUpperCase();
      const leafEntity = entityMap.get(guid);
      if (!leafEntity) return [];

      const ancestors = resolveCommunityAncestors(locationGuid);
      const leafName = String(leafEntity.name || '');
      const leafConfig = communityConfigStore.get(guid);
      const leafActive = leafConfig?.is_active === true || leafEntity?.is_active === true;

      const segments: CommunityPathSegment[] = [];
      if (leafName) {
        segments.push({ guid, name: leafName, isActive: leafActive });
      }
      for (let i = ancestors.length - 1; i >= 0; i--) {
        const a = ancestors[i];
        const aConfig = communityConfigStore.get(a.guid.toUpperCase());
        const aEntity = entityMap.get(a.guid.toUpperCase());
        const aActive = aConfig?.is_active === true || aEntity?.is_active === true;
        segments.push({ guid: a.guid, name: a.name, isActive: aActive });
      }
      return segments;
    },
    [entityMap]
  );

  /* ---- Navigate to a community page ---- */
  const handleCommunityClick = useCallback(
    (communityGuid: string) => {
      navigate(`/communities/${communityGuid}`);
    },
    [navigate]
  );

  /* ---- Card click → select project in split-screen (not navigate away) ---- */
  const handleCardClick = useCallback(
    (projectGuid: string) => {
      setSelectedGuid(projectGuid.toUpperCase());
      setMiniAutoOpenKpi(null);
      setActiveKpiTabIndex(-1);
      setTabRequestId((n) => n + 1);
    },
    []
  );

  /* ---- KPI shape click → select project + open to specific KPI tab ---- */
  const handleKpiClick = useCallback(
    (projectGuid: string, kpiTabIndex: number) => {
      setSelectedGuid(projectGuid.toUpperCase());
      setActiveKpiTabIndex(kpiTabIndex);
      setTabRequestId((n) => n + 1);
    },
    []
  );

  /* ---- DEV-1260: KPI shape click on a Mini-Project row → select + map
     the pill index (Summary, Schedule, Finance, Mission, Data) onto the
     panel's auto-open KPI modal. Summary has no modal — plain select.
     NOTE: do not pass 'summary' as autoOpenKpi; the type excludes it
     (see the pre-existing PortfolioPage.tsx:640 tsc error). ---- */
  const handleMiniKpiClick = useCallback(
    (miniProjectGuid: string, kpiTabIndex: number) => {
      const map: (MiniProjectAutoOpenKpi)[] = [
        null, 'schedule', 'budget', 'mission', 'dataReliability',
      ];
      setSelectedGuid(miniProjectGuid.toUpperCase());
      setMiniAutoOpenKpi(map[kpiTabIndex] ?? null);
      setActiveKpiTabIndex(-1);
      setTabRequestId((n) => n + 1);
    },
    []
  );

  /* ---- DEV-1260: card-image persistence for Mini-Project cards.
     Full-project cards persist through the projectConfigOverrides store;
     MPs PATCH the entity. The mutation event makes the App.tsx aggregator
     hooks refetch, which re-renders the card.

     Skip 2026-05-28 bug fix: this was previously writing to heroImageUrl
     because the MP schema had no card_image_url column, so the card crop
     was overwriting the original hero. Migration 191 added card_image_url
     to mini_projects and this handler now targets it. The detail-page
     hero stays untouched. ProjectCard's image-priority chain
     (card_image_url → hero → tile) handles the display fallback. ---- */
  const persistMiniHero = useCallback(
    (miniProjectGuid: string) => (url: string) => {
      updateMiniProject(miniProjectGuid, { cardImageUrl: url })
        .then(() => {
          dispatchMiniProjectMutated(miniProjectGuid);
          onProjectConfigChanged?.();
        })
        .catch(() => { /* best-effort; card keeps the old image */ });
    },
    [onProjectConfigChanged]
  );

  /* ---- Card hero image changed → propagate to parent for re-render ---- */
  const handleHeroChanged = useCallback(() => {
    onProjectConfigChanged?.();
  }, [onProjectConfigChanged]);

  /* ---- Close detail panel ---- */
  const handleCloseDetail = useCallback(() => {
    setSelectedGuid(null);
    setActiveKpiTabIndex(-1);
  }, []);

  /* ---- KPI tab change sync from detail panel ---- */
  const handleDetailKpiTabChange = useCallback((index: number) => {
    setActiveKpiTabIndex(index);
  }, []);

  /* ---- Responsive: container width detection ---- */
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const isNarrow = containerWidth < 700;

  /* ------------------------------------------------------------------ */
  /*  Left pane: cards                                                    */
  /* ------------------------------------------------------------------ */

  const cardsPane = (
    <div className={styles.cardsPane}>
      {/* ── Header bar ── */}
      {/* No page title here — the app header row already says "My
          Projects" (Skip 2026-06-07: duplicate removed). Search holds
          the start edge; the pill's auto margin pushes pill + New to
          the end edge. */}
      <div className={styles.headerBar}>
        {myProjects.length > 3 && (
          <input
            className={styles.search}
            type="text"
            placeholder={t('myProjects.searchProjects')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        )}

        {/* Show-completed pill — mirrors the KpiGridTable toolbar toggle
            (DEV-972 V5 design): accent dot signals state. */}
        <button
          type="button"
          className={`${styles.pillToggle} ${showCompleted ? styles.pillToggleOn : ''}`}
          onClick={toggleShowCompleted}
          aria-pressed={showCompleted}
          aria-label={showCompleted
            ? t('myProjectsCards.showCompletedOnTitle')
            : t('myProjectsCards.showCompletedOffTitle')}
          title={showCompleted
            ? t('myProjectsCards.showCompletedOnTitle')
            : t('myProjectsCards.showCompletedOffTitle')}
        >
          <span className={styles.pillToggleDot} aria-hidden="true" />
          <span>{t('myProjectsCards.showCompleted')}</span>
        </button>

        <button
          className={styles.newBtn}
          type="button"
          onClick={() => setShowCreateMini(true)}
        >
          + {t('myProjects.newMiniProject')}
        </button>

        <button
          className={styles.newBtn}
          type="button"
          onClick={() => navigate('/projects/new')}
        >
          + {t('myProjects.newProject')}
        </button>
      </div>

      {/* ── Cards body ── */}
      <div className={styles.body}>
        {filteredProjects.length === 0 && !searchText && (
          <div className={styles.empty}>
            {t('myProjectsCards.noProjects')}
          </div>
        )}
        {filteredProjects.length === 0 && searchText && (
          <div className={styles.empty}>
            {t('myProjects.noMatchingProjects')}
          </div>
        )}

        <div className={cardStyles.grid}>
          {filteredProjects.length <= 2 && <CreateProjectCard />}
          {filteredProjects.map((row: any) => {
            const guid = String(row?.project_guid || row?.project_GUID || '').toUpperCase();
            const project = row?.project || row || {};
            const isMini = !!row?._isMiniProject;
            const role = userRoleByProject.get(guid);
            const locationGuid = project.primary_location_entity_guid || '';
            const { flag, name } = resolveCountryInfo(locationGuid);
            return (
              <ProjectCard
                key={guid}
                project={{
                  project_guid: guid,
                  project_name: project.project_name || '(unnamed)',
                  card_image_url: project.card_image_url || null,
                  hero_image_url: project.hero_image_url || project.tile_image_url || null,
                  project_category: project.project_category || '',
                  project_status: project.project_status || '',
                  project_reach: project.project_reach || '',
                  description: project.description || project.shortDescription || '',
                  Community: project.Community || project.community_l3 || '',
                  primary_location_entity_guid: locationGuid,
                  visibility: project.visibility || '',
                }}
                countryFlag={flag}
                countryName={name}
                selected={guid === (selectedGuid || '').toUpperCase()}
                onClick={handleCardClick}
                communityPath={buildCommunityPath(locationGuid)}
                onCommunityClick={handleCommunityClick}
                kpiRow={row}
                onKpiClick={isMini ? handleMiniKpiClick : handleKpiClick}
                // DEV-1260: every MP here is mine (Lead) — hero menu always
                // available; FP keeps the owner_admin gate.
                canEditHero={isMini || role === 'owner_admin'}
                onHeroChanged={handleHeroChanged}
                persistHero={isMini ? persistMiniHero(guid) : undefined}
                // Skip 2026-05-28 — MP cards upload to the MP-specific
                // bucket; FP cards keep the default 'heroes' bucket. The
                // CardHeroMenu function is identical across tiers; only
                // the bucket name varies, matching how the detail-page
                // hero editors already parametrize per entity type.
                uploadBucket={isMini ? 'mini-project-hero' : undefined}
              />
            );
          })}
        </div>
      </div>
    </div>
  );

  /* ------------------------------------------------------------------ */
  /*  Right pane: compact detail panel                                    */
  /* ------------------------------------------------------------------ */

  const selectedIsMini = !!(selectedProjectRow as any)?._isMiniProject;

  const detailPane = selectedProjectRow ? (
    selectedIsMini ? (
      // DEV-1260: the "slightly different destination" — MP rows open the
      // compact MiniProjectDetailPanel (same panel Portfolio + Action
      // Center use) in the same split-screen.
      <MiniProjectDetailPanel
        miniProjectGuid={selectedGuid!}
        currentUserGuid={currentUserGuid}
        onClose={handleCloseDetail}
        autoOpenKpi={miniAutoOpenKpi}
        autoOpenKpiRequestId={tabRequestId}
      />
    ) : (
      <ProjectDetailPanel
        projectRow={selectedProjectRow}
        currentUserGuid={currentUserGuid}
        debugMode={debugMode}
        onProjectConfigChanged={onProjectConfigChanged}
        onClose={handleCloseDetail}
        kpiTabIndex={activeKpiTabIndex}
        tabRequestId={tabRequestId}
        onKpiTabChange={handleDetailKpiTabChange}
        viewMode="leader"
        initialWorkSubTab={restoreWorkSubTab as any}
      />
    )
  ) : null;

  /* ------------------------------------------------------------------ */
  /*  Render                                                              */
  /* ------------------------------------------------------------------ */

  /* Narrow viewport with a selected project → show detail full-screen
     with a back button to return to cards. */
  if (isNarrow && selectedProjectRow) {
    return (
      <div ref={containerRef} className={styles.page}>
        {selectedIsMini ? (
          <MiniProjectDetailPanel
            miniProjectGuid={selectedGuid!}
            currentUserGuid={currentUserGuid}
            onClose={handleCloseDetail}
            autoOpenKpi={miniAutoOpenKpi}
            autoOpenKpiRequestId={tabRequestId}
          />
        ) : (
          <ProjectDetailPanel
            projectRow={selectedProjectRow}
            currentUserGuid={currentUserGuid}
            debugMode={debugMode}
            onProjectConfigChanged={onProjectConfigChanged}
            onClose={handleCloseDetail}
            kpiTabIndex={activeKpiTabIndex}
            tabRequestId={tabRequestId}
            onKpiTabChange={handleDetailKpiTabChange}
            viewMode="leader"
          />
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={styles.page}>
      {selectedProjectRow ? (
        <ResizableSplitPane
          left={cardsPane}
          right={detailPane}
          initialRatio={0.45}
          minWidth={280}
        />
      ) : (
        cardsPane
      )}

      {showCreateMini && (
        <CreateMiniProjectModal
          currentUserGuid={currentUserGuid}
          communities={apiCommunityEntities || []}
          relationships={apiCommunityRelationships || []}
          onClose={() => setShowCreateMini(false)}
          onCreated={(miniProjectGuid) => {
            setShowCreateMini(false);
            dispatchMiniProjectMutated(miniProjectGuid);
            // Canonical detail route — ProjectDispatcher resolves Mini vs Full
            // by the GUID's project type (DEV-901). The `/mini-projects/`
            // namespace is being retired (Task #233).
            navigate(`/projects/${miniProjectGuid}`);
          }}
        />
      )}
    </div>
  );
}
